import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  ScrollView,
} from 'react-native';
import { useTheme } from '../theme/ThemeContext';
import { useThemedStyles } from '../theme/useThemedStyles';
import type { Theme } from '../theme/types';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function formatDateStr(year: number, month: number, day: number): string {
  const m = String(month + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
}

function parseDateStr(str: string): { year: number; month: number; day: number } {
  const parts = str.split('-');
  return {
    year: parseInt(parts[0], 10) || new Date().getFullYear(),
    month: (parseInt(parts[1], 10) || 1) - 1,
    day: parseInt(parts[2], 10) || 1,
  };
}

interface DatePickerModalProps {
  visible: boolean;
  value: string; // YYYY-MM-DD
  onSelect: (date: string) => void;
  onClose: () => void;
  title?: string;
}

export default function DatePickerModal({ visible, value, onSelect, onClose, title = 'Select Date' }: DatePickerModalProps) {
  const parsed = parseDateStr(value);
  const [year, setYear] = useState(parsed.year);
  const [month, setMonth] = useState(parsed.month);
  const [day, setDay] = useState(parsed.day);
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();

  const daysInMonth = getDaysInMonth(year, month);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Get day-of-week offset for the first day
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const blanks = Array.from({ length: firstDayOfWeek }, (_, i) => i);

  const handleConfirm = () => {
    const clampedDay = Math.min(day, daysInMonth);
    onSelect(formatDateStr(year, month, clampedDay));
  };

  const handlePrevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const handleNextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const isSelected = (d: number) => d === day;
  const isToday = (d: number) => {
    const now = new Date();
    return d === now.getDate() && month === now.getMonth() && year === now.getFullYear();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose} accessibilityViewIsModal={true}>
      <View style={styles.overlay}>
        <View style={styles.container} testID="date-picker-modal">
          <Text style={styles.title} accessibilityRole="header">{title}</Text>

          {/* Month/Year Navigation */}
          <View style={styles.navRow}>
            <TouchableOpacity
              onPress={handlePrevMonth}
              style={styles.navBtn}
              accessibilityRole="button"
              accessibilityLabel="Previous month"
              testID="date-picker.prev-month"
            >
              <Text style={styles.navBtnText} accessible={false}>{'\u2039'}</Text>
            </TouchableOpacity>
            <Text style={styles.monthYear} testID="date-picker.month-year">{MONTHS[month]} {year}</Text>
            <TouchableOpacity
              onPress={handleNextMonth}
              style={styles.navBtn}
              accessibilityRole="button"
              accessibilityLabel="Next month"
              testID="date-picker.next-month"
            >
              <Text style={styles.navBtnText} accessible={false}>{'\u203A'}</Text>
            </TouchableOpacity>
          </View>

          {/* Day-of-week headers */}
          <View style={styles.weekRow}>
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
              <Text key={i} style={styles.weekDay}>{d}</Text>
            ))}
          </View>

          {/* Calendar grid */}
          <View style={styles.calendarGrid}>
            {blanks.map((_, i) => (
              <View key={`blank-${i}`} style={styles.dayCell} />
            ))}
            {days.map((d) => (
              <TouchableOpacity
                key={d}
                style={[
                  styles.dayCell,
                  isSelected(d) && styles.dayCellSelected,
                  isToday(d) && !isSelected(d) && styles.dayCellToday,
                ]}
                onPress={() => setDay(d)}
                accessibilityRole="button"
                accessibilityLabel={`${MONTHS[month]} ${d}, ${year}${isToday(d) ? ', today' : ''}${isSelected(d) ? ', selected' : ''}`}
                accessibilityState={{ selected: isSelected(d) }}
                testID={`date-picker.day-${d}`}
              >
                <Text accessible={false} style={[
                  styles.dayText,
                  isSelected(d) && styles.dayTextSelected,
                  isToday(d) && !isSelected(d) && styles.dayTextToday,
                ]}>
                  {d}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Actions */}
          <View style={styles.actions}>
            <TouchableOpacity
              style={styles.cancelBtn}
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Cancel"
              testID="date-picker.cancel-button"
            >
              <Text style={styles.cancelBtnText} accessible={false}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.confirmBtn}
              onPress={handleConfirm}
              accessibilityRole="button"
              accessibilityLabel="Confirm date selection"
              testID="date-picker.confirm-button"
            >
              <Text style={styles.confirmBtnText} accessible={false}>Confirm</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const createStyles = (theme: Theme) => ({
  overlay: { flex: 1, backgroundColor: theme.colors.overlay, justifyContent: 'center' as const, alignItems: 'center' as const, padding: 24 },
  container: { backgroundColor: theme.colors.surface, borderRadius: 16, padding: 20, width: '100%' as const, maxWidth: 360 },
  title: { fontSize: 18, fontWeight: '700' as const, color: theme.colors.textPrimary, textAlign: 'center' as const, marginBottom: 16 },
  navRow: { flexDirection: 'row' as const, alignItems: 'center' as const, justifyContent: 'space-between' as const, marginBottom: 12 },
  navBtn: { width: 36, height: 36, borderRadius: 18, backgroundColor: theme.colors.bgTertiary, alignItems: 'center' as const, justifyContent: 'center' as const },
  navBtnText: { fontSize: 22, color: theme.colors.textSecondary, fontWeight: '300' as const },
  monthYear: { fontSize: 16, fontWeight: '600' as const, color: theme.colors.textPrimary },
  weekRow: { flexDirection: 'row' as const, marginBottom: 4 },
  weekDay: { flex: 1, textAlign: 'center' as const, fontSize: 12, fontWeight: '600' as const, color: theme.colors.textDisabled, paddingVertical: 4 },
  calendarGrid: { flexDirection: 'row' as const, flexWrap: 'wrap' as const },
  dayCell: { width: '14.28%' as unknown as number, aspectRatio: 1, alignItems: 'center' as const, justifyContent: 'center' as const },
  dayCellSelected: { backgroundColor: theme.colors.primary, borderRadius: 20 },
  dayCellToday: { borderWidth: 1, borderColor: theme.colors.primary, borderRadius: 20 },
  dayText: { fontSize: 14, color: theme.colors.textSecondary, fontWeight: '500' as const },
  dayTextSelected: { color: theme.colors.onPrimary, fontWeight: '700' as const },
  dayTextToday: { color: theme.colors.primary, fontWeight: '700' as const },
  actions: { flexDirection: 'row' as const, gap: 12, marginTop: 16 },
  cancelBtn: { flex: 1, paddingVertical: 12, borderRadius: 10, backgroundColor: theme.colors.bgTertiary, alignItems: 'center' as const },
  cancelBtnText: { fontSize: 15, fontWeight: '600' as const, color: theme.colors.textMuted },
  confirmBtn: { flex: 1, paddingVertical: 12, borderRadius: 10, backgroundColor: theme.colors.primary, alignItems: 'center' as const },
  confirmBtnText: { fontSize: 15, fontWeight: '700' as const, color: theme.colors.onPrimary },
});
